/**
 * Orders Page - View and Manage Orders
 * 
 * Displays orders from the API with status management.
 * Features auto-refresh, sound notifications, and browser alerts.
 * 
 * CRITICAL: This is the most important page for Tammy.
 * We auto-refresh and notify to ensure no orders are missed.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  addCalendarDays,
  calendarDaysFromToday,
  formatCalendarDateEnCA,
  formatTorontoDateTime,
  localYyyyMmDd,
  parseCalendarOrIso,
} from '../../utils/calendarDate';
import { Link } from 'react-router-dom';
import { 
  HiSearch, 
  HiFilter, 
  HiShoppingCart, 
  HiRefresh,
  HiPhone,
  HiMail,
  HiCalendar,
  HiClock,
  HiChevronDown,
  HiChevronUp,
  HiBell,
  HiVolumeUp,
  HiExclamation,
  HiPrinter,
  HiInbox,
  HiTag,
  HiClipboardList,
} from 'react-icons/hi';

import { getTotalCookiesFromName } from '../../utils/cookiePieces';
import AdminFlashAlert from '../components/AdminFlashAlert';
import AdminAlertSoundToggle from '../components/AdminAlertSoundToggle';
import {
  playNotificationSound,
  startMessageAlertSoundLoop,
  startOrderAlertSoundLoop,
  stopAllAlertSoundLoops,
  stopMessageAlertSoundLoop,
  stopOrderAlertSoundLoop,
  requestNotificationPermission,
  showBrowserNotification,
} from '../utils/adminNotifications';
import { filterOutSandboxTestOrders } from '../lib/sandboxTestOrders';
import { adminFetch, adminHeaders } from '../lib/adminApi';
import { useAdminAlertSound } from '../hooks/useAdminAlertSound';

// API URL - use environment variable or fallback to relative path
const API_URL = import.meta.env.VITE_API_URL || '';

// Auto-refresh interval (30 seconds)
const REFRESH_INTERVAL = 30000;

/** Persist sound + auto-print across sessions (Tammy's tablet/browser). */
const LS_ADMIN_ORDERS_AUTOPRINT = 'nannyb-admin-orders-auto-print';
/** Persist order history window (default: Today = actionable work; tiles widen to All). */
const LS_ADMIN_ORDERS_HISTORY = 'nannyb-admin-orders-history-range';

// Escape for HTML to avoid XSS when building print document
const escapeHtml = (s: string) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Open a print-friendly window for an order and trigger print dialog.
 * Exported for tests. Do NOT pass `noopener` in window.open features: Chrome 88+
 * returns null (no WindowProxy), so print would silently fail.
 */
export function openOrderPrintWindow(order: {
  order_number: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  items: Array<{ name: string; price: number; quantity: number; note?: string | null; notes?: string | null; customizations?: OrderItem['customizations'] }>;
  subtotal: number;
  hst?: number;
  tax?: number;
  total: number;
  pickup_date: string;
  pickup_time: string | null;
  special_instructions: string | null;
  cake_details?: string | null;
  created_at: string;
  source?: string;
  /** Optional — when present, ticket shows a clear PAID / NOT PAID stamp. */
  payment_status?: string | null;
  status?: string | null;
}, isAutoPrint = false) {
  const dateStr = (s: string) =>
    formatCalendarDateEnCA(s, { weekday: 'short', month: 'short', day: 'numeric' });
  const tax = order.hst ?? order.tax ?? 0;
  const money = (n: number) => `$${Number(n).toFixed(2)}`;
  const pickupYmd = String(order.pickup_date || '').slice(0, 10);
  const pickupLabel = `${dateStr(order.pickup_date)}${order.pickup_time ? ` at ${order.pickup_time}` : ''}`;
  const printedAt = formatTorontoDateTime();

  // Toronto business day — same clock Tammy uses for "today's pickups".
  const todayYmd = torontoYyyyMmDdToday();
  const tomorrowYmd = addCalendarDays(todayYmd, 1);
  let pickupUrgency = '';
  if (pickupYmd === todayYmd) pickupUrgency = 'TODAY';
  else if (pickupYmd === tomorrowYmd) pickupUrgency = 'TOMORROW';

  const paid = isBakeryOrderPaidUi(order.payment_status);
  // If payment_status was never passed, omit the stamp rather than scare her with NOT PAID.
  const showPayStamp = order.payment_status != null && String(order.payment_status).trim() !== '';
  const payStampLabel = paid ? 'PAID' : 'NOT PAID';
  const payStampClass = paid ? 'pay-stamp paid' : 'pay-stamp unpaid';

  // Pack | Qty | Item — amount is secondary; flavours/notes under each line are large.
  const itemsRows = (order.items || [])
    .map((i) => {
      const note = i.note ?? i.notes;
      const custStr = formatItemCustomizations(i.customizations);
      const extras: string[] = [];
      if (note) extras.push(`<div class="item-extra">${escapeHtml(note)}</div>`);
      if (custStr) extras.push(`<div class="item-extra">${escapeHtml(custStr)}</div>`);
      const lineTotal = Number(i.price) * (i.quantity || 1);
      return `<tr>
        <td class="pack">[ ]</td>
        <td class="qty">${escapeHtml(String(i.quantity || 1))}</td>
        <td class="item"><div class="item-name">${escapeHtml(i.name)}</div>${extras.join('')}</td>
        <td class="amt">${money(lineTotal)}</td>
      </tr>`;
    })
    .join('');

  let cakeHtml = '';
  if (order.cake_details) {
    try {
      const cake = typeof order.cake_details === 'string' ? JSON.parse(order.cake_details) : order.cake_details;
      if (cake && typeof cake === 'object') {
        const lines: string[] = [];
        if (cake.size) lines.push(`<li><span>Size</span> ${escapeHtml(cake.size)}</li>`);
        if (cake.flavor) lines.push(`<li><span>Flavor</span> ${escapeHtml(cake.flavor)}</li>`);
        if (cake.filling) lines.push(`<li><span>Filling</span> ${escapeHtml(cake.filling)}</li>`);
        if (cake.occasion) lines.push(`<li><span>Occasion</span> ${escapeHtml(cake.occasion)}</li>`);
        if (cake.cakeMessage) lines.push(`<li><span>Message</span> ${escapeHtml(cake.cakeMessage)}</li>`);
        if (cake.designNotes) lines.push(`<li><span>Design</span> ${escapeHtml(cake.designNotes)}</li>`);
        if (cake.servings) lines.push(`<li><span>Servings</span> ${escapeHtml(cake.servings)}</li>`);
        if (cake.dietaryNotes) lines.push(`<li><span>Dietary</span> ${escapeHtml(cake.dietaryNotes)}</li>`);
        if (lines.length) {
          cakeHtml = `<section class="notes-priority cake-block"><h2>Cake details</h2><ul>${lines.join('')}</ul></section>`;
        }
      }
    } catch {
      // ignore
    }
  }

  /**
   * Pack-slip hierarchy (phone / tablet / laptop):
   * 1) Notes + cake — largest, first
   * 2) Order lines — large qty/name/flavours
   * 3) Compact header (order #, paid, pickup)
   * 4) Quiet customer + money footer
   * Prefer borders over fill colours (phones often strip backgrounds).
   */
  const printCss = [
    'html,body{height:auto;overflow:visible;max-width:100%;}',
    '*{box-sizing:border-box;}',
    'body{font-family:system-ui,-apple-system,Segoe UI,sans-serif;margin:0 auto;padding:14px;max-width:720px;color:#111;font-size:11pt;line-height:1.3;word-wrap:break-word;overflow-wrap:anywhere;}',
    '.ticket-brand{font-size:8pt;letter-spacing:0.08em;text-transform:uppercase;color:#555;margin:0 0 2px 0;}',
    '.ticket-head-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px;}',
    'h1{font-size:16pt;margin:0;font-weight:700;}',
    'h2{font-size:10pt;letter-spacing:0.05em;text-transform:uppercase;color:#222;margin:0 0 8px 0;font-weight:700;}',
    '.meta{color:#555;font-size:9pt;margin:0 0 2px 0;}',
    '.ticket-head{border-bottom:1px solid #999;padding-bottom:8px;margin-bottom:10px;}',
    '.pay-stamp{font-size:10pt;font-weight:700;letter-spacing:0.06em;border:2px solid #111;padding:4px 8px;white-space:nowrap;flex-shrink:0;}',
    '.pay-stamp.unpaid{border-style:dashed;}',
    '.pickup-block{border:2px solid #111;padding:8px 12px;margin:0 0 12px 0;}',
    '.pickup-head{display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:2px;}',
    '.pickup-head h2{margin:0;font-size:8pt;color:#555;}',
    '.pickup-badge{font-size:10pt;font-weight:700;letter-spacing:0.08em;border:2px solid #111;padding:2px 7px;}',
    '.pickup-when{font-size:13pt;font-weight:700;margin:0;}',
    '.customer-line{font-size:10pt;margin:0 0 12px 0;color:#333;}',
    /* Notes / instructions — the first thing she must not miss */
    '.notes-priority{border:3px solid #111;border-left:8px solid #111;padding:12px 14px;margin:0 0 14px 0;}',
    '.notes-priority h2{font-size:11pt;margin:0 0 8px 0;}',
    '.notes-priority p{margin:0;font-size:14pt;font-weight:600;line-height:1.35;}',
    '.notes-priority ul{margin:0;padding:0;list-style:none;}',
    '.notes-priority li{margin:0 0 6px 0;font-size:13pt;line-height:1.35;}',
    '.notes-priority li span{font-weight:700;display:inline-block;min-width:5.5em;}',
    /* Order lines — main working area */
    '.order-section{margin:0 0 14px 0;}',
    '.order-section > h2{font-size:11pt;border-bottom:3px solid #111;padding-bottom:6px;margin-bottom:0;}',
    'table{width:100%;border-collapse:collapse;margin:0;font-size:13pt;}',
    'th{text-align:left;border-bottom:2px solid #111;padding:8px 6px;font-size:9pt;text-transform:uppercase;letter-spacing:0.04em;color:#444;}',
    'td{padding:12px 6px;border-bottom:1px solid #bbb;vertical-align:top;}',
    'th.pack,td.pack{width:2.2em;text-align:center;font-size:14pt;}',
    'th.qty,td.qty{width:2.6em;text-align:center;font-weight:700;font-size:16pt;}',
    'th.amt,td.amt{width:4.2em;text-align:right;white-space:nowrap;font-size:10pt;color:#444;}',
    '.item-name{font-size:14pt;font-weight:700;line-height:1.25;}',
    '.item-extra{font-size:13pt;font-weight:600;color:#111;margin-top:4px;line-height:1.3;}',
    '.totals{margin:8px 0 12px 0;max-width:220px;margin-left:auto;font-size:10pt;}',
    '.totals .row{display:flex;justify-content:space-between;gap:12px;padding:2px 0;}',
    '.totals .row.total{border-top:2px solid #111;margin-top:4px;padding-top:6px;font-size:12pt;font-weight:700;}',
    /* After packing — do not forget to update status (emails the customer). */
    '.ready-reminder{border:2px solid #111;padding:10px 12px;margin:0 0 12px 0;font-size:11pt;font-weight:600;line-height:1.35;}',
    '.ready-reminder .check{font-size:13pt;font-weight:700;margin-right:6px;}',
    '.ticket-foot{margin-top:12px;padding-top:8px;border-top:1px solid #999;font-size:8pt;color:#555;text-align:center;}',
    '@media print{',
    'html,body{height:auto!important;overflow:visible!important;margin:0;padding:8mm;max-width:none;}',
    '@page{size:auto;margin:10mm;}',
    'tr,.notes-priority,.pickup-block,.ready-reminder{page-break-inside:avoid;break-inside:avoid;}',
    '}',
  ].join(' ');

  const contactBits = [order.customer_name, order.customer_phone, order.customer_email]
    .filter((v) => v && String(v).trim())
    .map((v) => escapeHtml(String(v)));
  const customerLine =
    contactBits.length > 0
      ? `<p class="customer-line">${contactBits.join(' · ')}</p>`
      : '';
  const taxRow =
    tax > 0 ? `<div class="row"><span>HST</span><span>${money(tax)}</span></div>` : '';
  const instructionsHtml = order.special_instructions
    ? `<section class="notes-priority"><h2>Notes / instructions</h2><p>${escapeHtml(order.special_instructions)}</p></section>`
    : '';
  const payStampHtml = showPayStamp
    ? `<div class="${payStampClass}">${payStampLabel}</div>`
    : '';
  const pickupBadgeHtml = pickupUrgency
    ? `<span class="pickup-badge">${pickupUrgency}</span>`
    : '';
  // Skip the reminder if she already marked ready/complete — no nag on reprints.
  const statusLower = String(order.status || '').toLowerCase();
  const alreadyReady = statusLower === 'ready' || statusLower === 'completed';
  const readyReminderHtml = alreadyReady
    ? ''
    : `<section class="ready-reminder"><span class="check">[ ]</span> When this order is fully packed and ready, open Orders and tap <strong>Mark Ready for Pickup</strong> (customer gets an email).</section>`;

  // Pack slip: compact who/when → big notes → big order → ready reminder → quiet money.
  const html = `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>Order ${escapeHtml(order.order_number)} — Nanny B's Bakery</title>
<style>${printCss}</style>
</head><body>
<header class="ticket-head">
  <div class="ticket-head-top">
    <div>
      <p class="ticket-brand">Nanny B's Bakery</p>
      <h1>Order ${escapeHtml(order.order_number)}</h1>
    </div>
    ${payStampHtml}
  </div>
</header>
<section class="pickup-block">
  <div class="pickup-head"><h2>Pickup</h2>${pickupBadgeHtml}</div>
  <p class="pickup-when">${escapeHtml(pickupLabel)}</p>
</section>
${customerLine}
${instructionsHtml}
${cakeHtml}
<section class="order-section">
  <h2>Order</h2>
  <table>
    <thead><tr><th class="pack">Pack</th><th class="qty">Qty</th><th>Item</th><th class="amt">Amt</th></tr></thead>
    <tbody>${itemsRows}</tbody>
  </table>
</section>
${readyReminderHtml}
<div class="totals">
  <div class="row"><span>Subtotal</span><span>${money(order.subtotal)}</span></div>
  ${taxRow}
  <div class="row total"><span>Total</span><span>${money(order.total)}</span></div>
</div>
<footer class="ticket-foot">Printed ${printedAt.dateStr} at ${printedAt.timeStr} · Tick Pack as you fill · Keep with order</footer>
</body></html>`;

  /** Grow the print iframe to the full document so Chrome does not clip past one viewport. */
  const expandPrintIframeToContent = (iframe: HTMLIFrameElement, doc: Document) => {
    const measured = Math.max(
      doc.body?.scrollHeight || 0,
      doc.documentElement?.scrollHeight || 0
    );
    // Floor of 2000px covers long tickets when scrollHeight is 0 (jsdom) or still layouting.
    iframe.style.height = `${Math.max(measured, 2000)}px`;
    iframe.style.width = '100%';
  };

  /**
   * Hidden iframe print: no second window, so pop-up blockers usually do not block it.
   * Fallback: window.open (two-arg only — `noopener` in features makes Chromium return null).
   */
  const printViaNewWindow = (): boolean => {
    if (isAutoPrint) {
      // If auto-printing and iframe failed, do not show a blocking alert
      // because it will get stuck in the background and lock the page.
      return false;
    }
    const w = window.open('', '_blank');
    if (!w) {
      window.alert(
        'Could not print (pop-up blocked).\n\nTo fix this:\n1. Look for a "Pop-up blocked" icon in your browser\'s address bar.\n2. Tap it and select "Always allow pop-ups from this site".\n3. Try tapping "Print order" again.'
      );
      return false;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    window.setTimeout(() => {
      w.print();
      w.onafterprint = () => w.close();
      // Give Tammy time to confirm the dialog; do not yank the window too early.
      window.setTimeout(() => w.close(), 60_000);
    }, 250);
    return true;
  };

  const printViaIframe = (): boolean => {
    let iframe: HTMLIFrameElement | null = document.createElement('iframe');
    iframe.setAttribute('title', 'Order print preview');
    iframe.setAttribute('aria-hidden', 'true');
    // Start tiny; we expand to content height after write (height:100% crops tall orders).
    iframe.style.cssText =
      'position:fixed;left:0;top:0;width:100%;height:1px;border:0;opacity:0;pointer-events:none;z-index:-1';
    document.body.appendChild(iframe);
    const win = iframe.contentWindow;
    const doc = iframe.contentDocument;
    if (!win || !doc) {
      iframe.remove();
      return false;
    }
    try {
      doc.open();
      doc.write(html);
      doc.close();
      expandPrintIframeToContent(iframe, doc);
    } catch {
      iframe.remove();
      return false;
    }
    const cleanup = () => {
      if (iframe?.parentNode) iframe.parentNode.removeChild(iframe);
      iframe = null;
    };
    const runPrint = () => {
      try {
        // Re-measure after layout so multi-page sheets are fully in the iframe.
        if (iframe) expandPrintIframeToContent(iframe, doc);
        win.focus();
        win.print();
      } catch {
        cleanup();
        printViaNewWindow();
        return;
      }
      win.addEventListener('afterprint', cleanup, { once: true });
      window.setTimeout(cleanup, 120_000);
    };
    // Two rAFs: let layout settle so the sheet is not blank in Chrome/Safari.
    requestAnimationFrame(() => requestAnimationFrame(runPrint));
    return true;
  };

  if (printViaIframe()) return true;
  return printViaNewWindow();
}

type OrderStatus = 'pending' | 'pending_approval' | 'confirmed' | 'ready' | 'completed' | 'cancelled';

/** Per-item customizations from cart (e.g. sandwich bread, toppings). */
interface ItemCustomizationGroup {
  groupName?: string;
  selections?: Array<{ name: string }>;
}
interface OrderItem {
  name: string;
  price: number;
  quantity: number;
  category?: string;
  tax_applies?: boolean | null; // Per-item override from menu
  /** Customer's flavour/option choice (e.g. "Flavor: Brownie" or "2 Brownie, 1 Caramilk") */
  note?: string | null;
  notes?: string | null; // Alias from API
  /** Sandwich/customizable product choices (bread, toppings, etc.) */
  customizations?: Record<string, ItemCustomizationGroup> | null;
}

interface Order {
  id: string;
  order_number: string;
  source?: 'online' | 'square'; // Order source: online (website) or square (in-store)
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  items: OrderItem[];
  subtotal: number;
  hst: number; // Database column is named 'hst' not 'tax'
  tax?: number; // Legacy support
  total: number;
  pickup_date: string;
  pickup_time: string | null;
  special_instructions: string | null;
  status: OrderStatus;
  payment_status?: string; // 'paid' | 'pending' | etc. – always show so Tammy knows if order was paid
  notes: string | null;
  /** Custom cake order details (JSON string from DB) */
  cake_details?: string | null;
  created_at: string;
}

/** Parsed cake_details for custom cake orders. */
interface CakeDetails {
  size?: string;
  flavor?: string;
  filling?: string;
  occasion?: string;
  cakeMessage?: string;
  designNotes?: string;
  servings?: string;
  dietaryNotes?: string;
}

interface OrderStats {
  total: number;
  pending: number;
  /** Custom cake orders awaiting Tammy’s approval (matches DB status `pending_approval`). */
  pending_approval: number;
  confirmed: number;
  ready: number;
  completed: number;
  cancelled: number;
}

/** Coerce GET /api/orders/stats JSON (older servers may omit `pending_approval`). */
function normalizeOrderStats(raw: Record<string, unknown> | null | undefined): OrderStats {
  const r = raw || {};
  const n = (key: string) =>
    typeof r[key] === 'number' && !Number.isNaN(r[key] as number) ? (r[key] as number) : 0;
  return {
    total: n('total'),
    pending: n('pending'),
    pending_approval: n('pending_approval'),
    confirmed: n('confirmed'),
    ready: n('ready'),
    completed: n('completed'),
    cancelled: n('cancelled'),
  };
}

/** Today YYYY-MM-DD in America/Toronto — aligns with GET /api/orders `status=completed` cohort. */
function torontoYyyyMmDdToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;
  return `${y}-${m}-${d}`;
}

/** Same literals as backend `PAID_PAYMENT_STATUS_IN` (Postgres is case-sensitive). */
const BAKERY_PAID_PAYMENT_STATUSES = new Set(['paid', 'Paid', 'PAID']);

function isBakeryOrderPaidUi(payment_status: string | null | undefined): boolean {
  return BAKERY_PAID_PAYMENT_STATUSES.has(String(payment_status ?? ''));
}

/**
 * Rows the backend includes for `?status=completed`: status completed, or paid + pending/confirmed/ready
 * with pickup calendar date strictly before Toronto today (string YYYY-MM-DD compare).
 */
export function orderMatchesCompletedAdminCohort(order: Order): boolean {
  if (order.status === 'completed') return true;
  const paid = isBakeryOrderPaidUi(order.payment_status);
  if (!paid || !['pending', 'confirmed', 'ready'].includes(order.status)) return false;
  const py = String(order.pickup_date || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(py)) return false;
  return py < torontoYyyyMmDdToday();
}

export type BusinessHourRow = { day: string; open: string; close: string; isClosed: boolean };
export type ClosureRow = { closure_date: string; label?: string | null };

/** Map pickup YYYY-MM-DD to businessHours row index (Mon=0 … Sun=6). */
export function getDayIndexForPickupYmd(dateStr: string): number {
  const d = new Date(`${dateStr.slice(0, 10)}T12:00:00`);
  const jsDay = d.getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
}

/** True when pickup falls on a weekly closed day or a one-off closure date. */
export function orderPickupOnClosedDay(
  order: Pick<Order, 'status' | 'pickup_date'>,
  closureDateSet: Set<string>,
  businessHours: BusinessHourRow[]
): boolean {
  if (order.status === 'cancelled') return false;
  const ymd = (order.pickup_date || '').slice(0, 10);
  if (!ymd) return false;
  if (closureDateSet.has(ymd)) return true;
  const dayInfo = businessHours[getDayIndexForPickupYmd(ymd)];
  return dayInfo?.isClosed === true;
}

/** Human-readable reason Tammy can read when calling the customer. */
export function pickupClosedDayReason(
  ymd: string,
  closures: ClosureRow[],
  businessHours: BusinessHourRow[]
): string {
  const closure = closures.find((c) => (c.closure_date || '').slice(0, 10) === ymd);
  if (closure) {
    return closure.label ? `Holiday closure: ${closure.label}` : 'Holiday / closure day';
  }
  const dayInfo = businessHours[getDayIndexForPickupYmd(ymd)];
  if (dayInfo?.isClosed) {
    return dayInfo.day ? `Regular closed day (${dayInfo.day})` : 'Regular closed day';
  }
  return 'Closed day';
}

/** Format item customizations (e.g. sandwich bread, toppings) for display. */
function formatItemCustomizations(customizations: OrderItem['customizations']): string {
  if (!customizations || typeof customizations !== 'object') return '';
  const parts: string[] = [];
  for (const group of Object.values(customizations)) {
    if (!group || typeof group !== 'object') continue;
    const name = group.groupName || 'Options';
    const selections = group.selections;
    if (Array.isArray(selections) && selections.length > 0) {
      const names = selections.map((s: { name?: string }) => s?.name ?? '').filter(Boolean);
      if (names.length) parts.push(`${name}: ${names.join(', ')}`);
    }
  }
  return parts.join('; ');
}

// Tax rules: same as Order.tsx / backend – CRA Ontario. Only baked goods count toward 6+; sandwiches always taxable.
function getPiecesForItem(i: OrderItem): number {
  const cat = (i.category ?? '').toLowerCase();
  const name = (i.name ?? '').toLowerCase();
  const qty = i.quantity || 1;
  if (cat.includes('stuffed') || name.includes('stuffed')) {
    return getTotalCookiesFromName(i.name) * qty;
  }
  return qty;
}
const isBakedGoodForSixRule = (i: OrderItem) =>
  !(i.category ?? '').toLowerCase().includes('sandwiches & savory');
const isCake = (i: OrderItem) => (i.category ?? '').toLowerCase().includes('cake');
function isItemTaxableByCategory(i: OrderItem, bakedGoodsZeroRated: boolean): boolean {
  if (isCake(i)) return false;
  if (!isBakedGoodForSixRule(i)) return true;
  return !bakedGoodsZeroRated;
}
function getTaxableSubtotalFromItems(items: OrderItem[]): number {
  const totalBakedPieces = items
    .filter(isBakedGoodForSixRule)
    .reduce((sum, i) => sum + getPiecesForItem(i), 0);
  const bakedGoodsZeroRated = totalBakedPieces >= 6;
  return items
    .filter((i) => {
      if (i.tax_applies === false) return false;
      if (i.tax_applies === true) return true;
      return isItemTaxableByCategory(i, bakedGoodsZeroRated);
    })
    .reduce((sum, i) => sum + Number(i.price) * (i.quantity || 1), 0);
}

// Status config (pending_approval = custom cakes waiting for approval).
// tileTitle: hover / screen-reader hint — "Completed" especially is often confused with "paid".
const statusConfig: Record<
  OrderStatus,
  { color: string; bg: string; label: string; tileTitle: string }
> = {
  pending: {
    color: 'text-amber-700',
    bg: 'bg-amber-100',
    label: 'Pending',
    tileTitle: 'Paid orders still in Pending (unusual after checkout; may need a status update).',
  },
  pending_approval: {
    color: 'text-purple-700',
    bg: 'bg-purple-100',
    label: 'Needs Approval',
    tileTitle: 'Custom cake requests awaiting your approval before the customer can pay.',
  },
  confirmed: {
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    label: 'Confirmed',
    tileTitle: 'Paid and confirmed for prep — normal stage right after payment until Ready or pickup.',
  },
  ready: {
    color: 'text-green-700',
    bg: 'bg-green-100',
    label: 'Ready for Pickup',
    tileTitle: 'Paid orders you marked ready for the customer to pick up.',
  },
    completed: {
    color: 'text-gray-700',
    bg: 'bg-gray-100',
    label: 'Completed',
    tileTitle:
      'Marked Complete, or paid with pickup date before today (Toronto) even if the row still says Confirmed/Ready. Use the Completed filter to see them all.',
  },
  cancelled: {
    color: 'text-red-700',
    bg: 'bg-red-100',
    label: 'Cancelled',
    tileTitle: 'Cancelled orders (paid or unpaid depending on row).',
  },
};

// Payment status: show Paid vs Not paid clearly
const isPaid = (order: Order) => (order.payment_status || '').toLowerCase() === 'paid';

// Payment info API response (for dropdown details)
interface PaymentInfoResponse {
  orderId: string;
  payment_status: string | null;
  source: string | null;
  transaction?: {
    id?: string;
    status?: string;
    amount?: { amount: number; currencyCode?: string };
    receiptUrl?: string;
    tenders?: Array<{ id?: string; amount?: { amount: number }; type?: string; createdAt?: string }>;
  } | null;
  transactionError?: string;
}

/** Order placed window for the admin list API: Toronto day, rolling N days, or `period=all` (since shop open 2025, capped per request). */
export type HistoryRange = 'today' | '7' | '30' | '90' | '365' | '1095' | 'all';

const VALID_HISTORY_RANGES: HistoryRange[] = ['today', '7', '30', '90', '365', '1095', 'all'];

/**
 * Default `today` = day-to-day work (placed today + today's pickups + upcoming active pipeline).
 * Status tiles widen to `all` so counts match the list. Remember last choice in localStorage.
 */
function readPersistedHistoryRange(): HistoryRange {
  if (typeof window === 'undefined') return 'today';
  try {
    const v = window.localStorage.getItem(LS_ADMIN_ORDERS_HISTORY);
    if (v && (VALID_HISTORY_RANGES as readonly string[]).includes(v)) return v as HistoryRange;
  } catch {
    /* ignore */
  }
  return 'today';
}

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [stats, setStats] = useState<OrderStats | null>(null);
  /** Shown when tile counts fail to load — avoids silently showing zeros. */
  const [statsLoadWarning, setStatsLoadWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'all'>('all');
  /** "Never miss an order" help — collapsed by default so new/pending orders stay the focus. */
  const [neverMissHelpOpen, setNeverMissHelpOpen] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const { soundEnabled } = useAdminAlertSound();
  const [orderFlash, setOrderFlash] = useState({ open: false, count: 1 });
  const [messageFlash, setMessageFlash] = useState({ open: false, count: 1 });
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [paymentDetailsOpen, setPaymentDetailsOpen] = useState<string | null>(null);
  const [paymentDetailsCache, setPaymentDetailsCache] = useState<Record<string, { loading: boolean; data: PaymentInfoResponse | null; error: string | null }>>({});
  const [soundUnlocked, setSoundUnlocked] = useState(false);
  const [autoPrintNewOrders, setAutoPrintNewOrders] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(LS_ADMIN_ORDERS_AUTOPRINT) === '1';
    } catch {
      return false;
    }
  });
  const [pendingPrintOrder, setPendingPrintOrder] = useState<Order | null>(null);
  const [unseenNewOrders, setUnseenNewOrders] = useState(0);
  const [unseenNewMessages, setUnseenNewMessages] = useState(0);
  const [historyRange, setHistoryRange] = useState<HistoryRange>(() => readPersistedHistoryRange());
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  /** Total paid orders in the selected date window (from API merge). */
  const [historyTotal, setHistoryTotal] = useState(0);
  /** One-shot: “All paid / picked up” sets Completed + full history without double-fetch from status effect. */
  const suppressStatusRefetchRef = useRef(false);
  /** Bumps on each list reset so stale polls / double-fetches cannot overwrite a filter change. */
  const ordersFetchEpochRef = useRef(0);
  /** True while the paid/picked-up archive button is auto-paginating. */
  const [loadingPaidArchive, setLoadingPaidArchive] = useState(false);
  const loadingPaidArchiveRef = useRef(false);
  const loadingRef = useRef(true);

  // Need-attention strip: contact messages, closures, promos ending soon, today's pickups, pending/approval
  const [contactStats, setContactStats] = useState<{ new: number }>({ new: 0 });
  const [closures, setClosures] = useState<Array<{ closure_date: string; label?: string | null }>>([]);
  const [businessHours, setBusinessHours] = useState<Array<{ day: string; open: string; close: string; isClosed: boolean }>>([]);
  const [promosEndingSoon, setPromosEndingSoon] = useState<Array<{ id: string; title: string; end_date: string | null }>>([]);
  /** Optional archive filter; closed-day orders no longer create a persistent alert banner. */
  const [showClosedDayOnly, setShowClosedDayOnly] = useState(false);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  /** True after first unlock so we do not spam setState on every pointerdown. */
  const audioUnlockedOnceRef = useRef(false);
  /**
   * Order count from GET /api/orders/stats (paid rows only — same cohort as the list).
   * Using this instead of merged list `sources.total` avoids false/missed alerts when
   * Square in-store sync returns a different row count between polls.
   */
  const lastSeenStatsTotalRef = useRef<number | null>(null);
  const lastSeenContactNewRef = useRef<number | null>(null);
  const ordersRef = useRef<Order[]>([]);
  const prevHistoryRangeRef = useRef(historyRange);
  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    loadingPaidArchiveRef.current = loadingPaidArchive;
  }, [loadingPaidArchive]);


  useEffect(() => {
    try {
      window.localStorage.setItem(LS_ADMIN_ORDERS_AUTOPRINT, autoPrintNewOrders ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [autoPrintNewOrders]);

  // Keep urgency visible even when sound is missed: show counts in the browser tab title.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const baseTitle = 'Orders | Admin';
    const bits: string[] = [];
    if (unseenNewOrders > 0) bits.push(`${unseenNewOrders} new order${unseenNewOrders > 1 ? 's' : ''}`);
    if (unseenNewMessages > 0) bits.push(`${unseenNewMessages} new message${unseenNewMessages > 1 ? 's' : ''}`);
    document.title = bits.length ? `(${bits.join(' • ')}) ${baseTitle}` : baseTitle;
    return () => {
      document.title = baseTitle;
    };
  }, [unseenNewOrders, unseenNewMessages]);

  // Unlock audio on user gesture (required by browsers); then sound can play when new orders arrive.
  const unlockAudio = useCallback(() => {
    if (typeof window === 'undefined') return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    if (!audioContextRef.current) audioContextRef.current = new Ctx();
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
  }, []);

  // One click anywhere on the page unlocks sound (browser requirement). No need to find a specific button.
  const handlePageClickForSound = useCallback(() => {
    if (soundUnlocked) return;
    unlockAudio();
    audioUnlockedOnceRef.current = true;
    setSoundUnlocked(true);
  }, [soundUnlocked, unlockAudio]);

  const handleTestSound = useCallback(() => {
    unlockAudio();
    audioUnlockedOnceRef.current = true;
    setSoundUnlocked(true);
    playNotificationSound(audioContextRef.current);
  }, [unlockAudio]);

  /** Tammy dismisses the order popup — also stops the looping alarm. */
  const dismissOrderFlash = useCallback(() => {
    stopOrderAlertSoundLoop();
    setOrderFlash((f) => ({ ...f, open: false }));
  }, []);

  /** Tammy dismisses the message popup — also stops the looping chime. */
  const dismissMessageFlash = useCallback(() => {
    stopMessageAlertSoundLoop();
    setMessageFlash((f) => ({ ...f, open: false }));
  }, []);

  useEffect(() => () => stopAllAlertSoundLoops(), []);

  // Fetch orders and need-attention data (contact messages, closures, promos ending soon)
  const fetchOrders = useCallback(
    async (
      showLoading = true,
      opts?: {
        append?: boolean;
        historyOverride?: HistoryRange;
        resetList?: boolean;
        /** Avoid waiting for React state when batch-loading (e.g. paid archive button). */
        statusOverride?: OrderStatus | 'all';
        /** Larger page size when bulk-loading (capped at 500). */
        pageLimit?: number;
      }
    ) => {
    const append = opts?.append === true;
    const range = opts?.historyOverride ?? historyRange;
    if (!append) prevHistoryRangeRef.current = range;

    // Non-append loads supersede in-flight polls (e.g. auto-refresh on Today overwriting paid archive).
    const fetchEpoch = append ? ordersFetchEpochRef.current : ++ordersFetchEpochRef.current;

    const pageSizeDefault = 50;
    const explicitLimit = opts?.pageLimit;
    const limit =
      explicitLimit != null
        ? Math.min(500, Math.max(25, explicitLimit))
        : append
          ? pageSizeDefault
          : opts?.resetList
            ? pageSizeDefault
            : Math.min(500, Math.max(pageSizeDefault, ordersRef.current.length || pageSizeDefault));
    const loaded = append ? ordersRef.current.length : 0;
    const offset = append ? loaded : 0;
    const qs = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (range === 'today') {
      qs.set('period', 'today');
    } else if (range === 'all') {
      qs.set('period', 'all');
    } else {
      qs.set('days', String(range));
    }
    // Server-side status filter so tabs like Completed are not empty just because the newest
    // page is all pending/confirmed (list was sorted by created_at only; stats are global).
    const statusForQuery = opts?.statusOverride ?? statusFilter;
    if (statusForQuery !== 'all') {
      qs.set('status', statusForQuery);
    }
    if (showLoading && !append) setLoading(true);
    if (append) setLoadingMore(true);
    const base = API_URL || '';
    const ordersUrl = `${base}/api/orders?${qs.toString()}`;
    const statsUrl = `${base}/api/orders/stats`;
    const contactStatsUrl = `${base}/api/contact/stats`;
    const closuresUrl = `${base}/api/settings/closures`;
    const hoursUrl = `${base}/api/settings/hours`;
    const promosUrl = `${base}/api/promos`;
    try {
      const [ordersRes, statsRes, contactRes, closuresRes, hoursRes, promosRes] = await Promise.all([
        adminFetch(ordersUrl).catch(err => {
          console.error('Failed to fetch orders:', err);
          throw new Error(`Network error: ${err.message}`);
        }),
        adminFetch(statsUrl).catch((err) => {
          console.error('Failed to fetch order stats:', err);
          return null;
        }),
        adminFetch(contactStatsUrl).catch(() => ({ ok: false, json: async () => ({ new: 0 }) })),
        adminFetch(closuresUrl).catch(() => ({ ok: false, json: async () => ({ closures: [] }) })),
        adminFetch(hoursUrl).catch(() => ({ ok: false, json: async () => ({ hours: [] }) })),
        adminFetch(promosUrl).catch(() => ({ ok: false, json: async () => ([]) })),
      ]);

      if (fetchEpoch !== ordersFetchEpochRef.current) {
        return { hasMore: false };
      }

      if (!ordersRes.ok) {
        const errorText = await ordersRes.text().catch(() => 'Unknown error');
        throw new Error(`Failed to fetch orders: ${ordersRes.status} ${errorText}`);
      }

      const ordersData = await ordersRes.json();
      let statsData: OrderStats | null = null;
      if (statsRes && statsRes.ok) {
        statsData = normalizeOrderStats(await statsRes.json());
        setStatsLoadWarning(null);
      } else if (statsRes) {
        const detail = await statsRes.text().catch(() => '');
        console.error('Order stats request failed:', statsRes.status, detail);
        setStatsLoadWarning(
          'Order counts did not load (server hiccup). Tap Refresh — your order list below may still be correct.'
        );
      } else {
        setStatsLoadWarning('Order counts could not be reached. Check your connection and tap Refresh.');
      }
      const newOrders = filterOutSandboxTestOrders<Order>((ordersData.orders ?? []) as Order[]);
      const statsTotal =
        statsData != null && typeof statsData.total === 'number' && !Number.isNaN(statsData.total)
          ? statsData.total
          : null;

      // New-order trigger: compare paid online order count (stats), not merged Square+online list length.
      const prevStatsTotal = lastSeenStatsTotalRef.current;
      if (prevStatsTotal !== null && statsTotal !== null && statsTotal > prevStatsTotal) {
        const newCount = statsTotal - prevStatsTotal;
        setOrderFlash({ open: true, count: newCount });
        setUnseenNewOrders((prev) => prev + newCount);

        if (soundEnabled) {
          if (!audioContextRef.current) unlockAudio();
          startOrderAlertSoundLoop(audioContextRef.current);
        }

        showBrowserNotification(
          '🧁 New Order!',
          `You have ${newCount} new order${newCount > 1 ? 's' : ''}!`,
          { tag: 'new-order', requireInteraction: true },
        );

        if (autoPrintNewOrders && newOrders.length > 0) {
          const ok = openOrderPrintWindow(newOrders[0], true);
          if (!ok) setPendingPrintOrder(newOrders[0]);
        }
      }
      if (statsTotal !== null) {
        lastSeenStatsTotalRef.current = statsTotal;
      }

      // Contact messages: notify when new count increases (after first load)
      if (contactRes.ok) {
        const contactData = await contactRes.json();
        const newCount = contactData.new ?? 0;
        if (lastSeenContactNewRef.current !== null && newCount > lastSeenContactNewRef.current) {
          const delta = newCount - lastSeenContactNewRef.current;
          setUnseenNewMessages((prev) => prev + Math.max(1, delta));
          setMessageFlash({ open: true, count: Math.max(1, delta) });
          if (soundEnabled) {
            if (!audioContextRef.current) unlockAudio();
            startMessageAlertSoundLoop(audioContextRef.current);
          }
          showBrowserNotification(
            '📩 New contact message',
            `${newCount} new message${newCount > 1 ? 's' : ''}. Mark Replied after callback (email one-tap). Pay links: log in to Messages.`,
            { tag: 'new-message', requireInteraction: true },
          );
        }
        lastSeenContactNewRef.current = newCount;
        setContactStats({ new: newCount });
      }

      // Closures (holiday / one-off)
      if (closuresRes.ok) {
        const closuresData = await closuresRes.json();
        setClosures(closuresData.closures || []);
      }

      if (hoursRes.ok) {
        const hoursData = await hoursRes.json();
        if (Array.isArray(hoursData.hours)) setBusinessHours(hoursData.hours);
      }

      // Promos ending in next 7 calendar days (local)
      if (promosRes.ok) {
        const promosData = await promosRes.json();
        const promos = Array.isArray(promosData) ? promosData : (promosData.promos || []);
        const today = localYyyyMmDd();
        const weekEnd = addCalendarDays(today, 7);
        const ending = promos.filter((p: { end_date?: string | null; is_active?: boolean }) => {
          if (!p.end_date || !p.is_active) return false;
          const endPart = String(p.end_date).split('T')[0];
          return /^\d{4}-\d{2}-\d{2}$/.test(endPart) && endPart >= today && endPart <= weekEnd;
        });
        setPromosEndingSoon(ending.map((p: { id: string; title?: string; end_date?: string | null }) => ({
          id: p.id,
          title: p.title || 'Promo',
          end_date: p.end_date || null,
        })));
      }

      if (append) {
        setOrders((prev) => {
          const ids = new Set(prev.map((o) => o.id));
          const extra = newOrders.filter((o: Order) => !ids.has(o.id));
          return [...prev, ...extra];
        });
      } else {
        setOrders(newOrders);
      }
      if (fetchEpoch !== ordersFetchEpochRef.current) {
        return { hasMore: false };
      }
      setOrdersHasMore(!!ordersData.pagination?.hasMore);
      setHistoryTotal(ordersData.sources?.total ?? newOrders.length);
      if (statsData != null) {
        setStats(statsData);
      }
      setLastRefresh(new Date());
      setError(null);
      return { hasMore: !!ordersData.pagination?.hasMore };
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
      return { hasMore: false };
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  },
  [historyRange, soundEnabled, autoPrintNewOrders, statusFilter]
);

  /** Full archive + Completed cohort, then auto-append pages until the server has no more (matches DB-backed list). */
  const loadPaidPickedUpArchive = useCallback(async () => {
    unlockAudio();
    audioUnlockedOnceRef.current = true;
    setSoundUnlocked(true);
    suppressStatusRefetchRef.current = true;
    setSearchQuery('');
    setHistoryRange('all');
    try {
      window.localStorage.setItem(LS_ADMIN_ORDERS_HISTORY, 'all');
    } catch {
      /* ignore */
    }
    setStatusFilter('completed');
    ordersRef.current = [];
    setOrders([]);
    setLoadingPaidArchive(true);
    setError(null);
    try {
      let result = await fetchOrders(true, {
        historyOverride: 'all',
        statusOverride: 'completed',
        resetList: true,
        pageLimit: 200,
      });
      let guard = 0;
      while (result?.hasMore && guard++ < 400) {
        result = await fetchOrders(false, {
          append: true,
          historyOverride: 'all',
          statusOverride: 'completed',
          pageLimit: 200,
        });
      }
    } finally {
      suppressStatusRefetchRef.current = false;
      setLoadingPaidArchive(false);
    }
  }, [fetchOrders, unlockAudio]);

  // Keep latest fetchOrders for status-only effects without re-running when sound/print toggles.
  const fetchOrdersRef = useRef(fetchOrders);
  fetchOrdersRef.current = fetchOrders;

  // When Tammy picks a status tile or dropdown, refetch with ?status= so the first page matches
  // (avoids "Completed: 50" tile but 0 rows until many Load more clicks).
  const skipStatusFilterEffectOnce = useRef(true);
  useEffect(() => {
    if (skipStatusFilterEffectOnce.current) {
      skipStatusFilterEffectOnce.current = false;
      return;
    }
    if (suppressStatusRefetchRef.current) {
      return;
    }
    void fetchOrdersRef.current(true, { resetList: true });
  }, [statusFilter]);

  // Unlock Web Audio on the first tap/key anywhere (capture phase).
  // Needed because inner controls use stopPropagation (e.g. Print), which skipped the wrapper onClick.
  useEffect(() => {
    const onFirstInteract = () => {
      unlockAudio();
      if (!audioUnlockedOnceRef.current) {
        audioUnlockedOnceRef.current = true;
        setSoundUnlocked(true);
      }
    };
    document.addEventListener('pointerdown', onFirstInteract, true);
    document.addEventListener('keydown', onFirstInteract, true);
    return () => {
      document.removeEventListener('pointerdown', onFirstInteract, true);
      document.removeEventListener('keydown', onFirstInteract, true);
    };
  }, [unlockAudio]);

  // One-time: notification permission + first load
  useEffect(() => {
    requestNotificationPermission();
    fetchOrders(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only initial load
  }, []);

  // Poll orders while auto-refresh is on; interval tracks latest fetchOrders (e.g. after history range change)
  useEffect(() => {
    if (!autoRefreshEnabled) {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
      return;
    }
    refreshIntervalRef.current = setInterval(() => {
      if (
        loadingPaidArchiveRef.current ||
        loadingRef.current ||
        suppressStatusRefetchRef.current
      ) {
        return;
      }
      fetchOrders(false);
    }, REFRESH_INTERVAL);
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    };
  }, [autoRefreshEnabled, fetchOrders]);

  const onHistoryRangeChange = useCallback(
    (r: HistoryRange) => {
      setHistoryRange(r);
      try {
        window.localStorage.setItem(LS_ADMIN_ORDERS_HISTORY, r);
      } catch {
        /* ignore */
      }
      ordersRef.current = [];
      setOrders([]);
      fetchOrders(true, { historyOverride: r, resetList: true });
    },
    [fetchOrders]
  );

  /**
   * Status tiles/dropdown use all-time counts. If history is still "Today" (or 7/30…),
   * filtering to Completed/Confirmed can return an empty list even when the tile shows 5.
   * Widen to All orders so clicking a tile always shows those rows.
   */
  const applyStatusFilter = useCallback(
    (next: OrderStatus | 'all') => {
      suppressStatusRefetchRef.current = true;
      if (next === 'all') {
        setStatusFilter('all');
        void fetchOrders(true, { statusOverride: 'all', resetList: true }).finally(() => {
          suppressStatusRefetchRef.current = false;
        });
        return;
      }
      if (historyRange !== 'all') {
        setHistoryRange('all');
        try {
          window.localStorage.setItem(LS_ADMIN_ORDERS_HISTORY, 'all');
        } catch {
          /* ignore */
        }
        ordersRef.current = [];
        setOrders([]);
        setStatusFilter(next);
        void fetchOrders(true, {
          historyOverride: 'all',
          statusOverride: next,
          resetList: true,
        }).finally(() => {
          suppressStatusRefetchRef.current = false;
        });
        return;
      }
      setStatusFilter(next);
      void fetchOrders(true, { statusOverride: next, resetList: true }).finally(() => {
        suppressStatusRefetchRef.current = false;
      });
    },
    [fetchOrders, historyRange]
  );

  /** Open the full archive and show only pickups scheduled on a configured closed day. */
  const toggleClosedDayFilter = useCallback(() => {
    if (showClosedDayOnly) {
      setShowClosedDayOnly(false);
      return;
    }

    suppressStatusRefetchRef.current = true;
    setShowClosedDayOnly(true);
    setSearchQuery('');
    setStatusFilter('all');
    setHistoryRange('all');
    try {
      window.localStorage.setItem(LS_ADMIN_ORDERS_HISTORY, 'all');
    } catch {
      /* ignore */
    }
    ordersRef.current = [];
    setOrders([]);
    void fetchOrders(true, {
      historyOverride: 'all',
      statusOverride: 'all',
      resetList: true,
      pageLimit: 200,
    }).finally(() => {
      suppressStatusRefetchRef.current = false;
    });
  }, [fetchOrders, showClosedDayOnly]);

  // Update order status (Ready / cancel / cake approve may also email the customer).
  const updateStatus = async (
    orderId: string,
    newStatus: OrderStatus,
    opts?: { resendEmail?: boolean }
  ) => {
    // Complete is easy to tap by mistake on a phone — confirm first.
    if (newStatus === 'completed') {
      const ok = window.confirm(
        'Mark this order as picked up?\n\nThis does not email the customer. Use “Mark Ready for Pickup” when the order is packed and ready (that email notifies them).'
      );
      if (!ok) return;
    }

    try {
      const statusUrl = API_URL ? `${API_URL}/api/orders/${orderId}/status` : `/api/orders/${orderId}/status`;
      const body: { status: OrderStatus; resendEmail?: boolean } = { status: newStatus };
      if (opts?.resendEmail) body.resendEmail = true;
      const response = await adminFetch(statusUrl, {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error('Failed to update status');

      const data = (await response.json().catch(() => ({}))) as {
        status?: OrderStatus;
        customerNotification?: {
          kind?: string;
          success?: boolean;
          to?: string;
          reason?: string;
          error?: string;
        };
      };

      // Functional update avoids stale closure if multiple refreshes ran while the request was in flight.
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: data.status || newStatus } : o))
      );

      // Tell Tammy whether the customer email actually went out (silent failures caused confusion).
      const note = data.customerNotification;
      if (note && (note.kind === 'ready' || note.kind === 'cancelled' || note.kind === 'cake_pay')) {
        if (note.success) {
          window.alert(
            `✅ Status updated.\n\nCustomer email sent to ${note.to || 'the customer'}.`
          );
        } else {
          window.alert(
            `⚠️ Status updated, but the customer email was NOT sent.\n\n${note.reason || note.error || 'Unknown email error'}\n\nCheck spam, or ask Humberto to verify Mailgun/SMTP on the server.`
          );
        }
      }

      // Refresh stats
      const statsUrl = API_URL ? `${API_URL}/api/orders/stats` : '/api/orders/stats';
      const statsRes = await adminFetch(statsUrl);
      if (statsRes.ok) {
        setStats(normalizeOrderStats(await statsRes.json()));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update order status';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  // Fetch payment/transaction details for an order (for dropdown)
  const fetchPaymentInfo = useCallback(async (orderId: string) => {
    setPaymentDetailsCache(prev => ({ ...prev, [orderId]: { loading: true, data: null, error: null } }));
    try {
      const url = API_URL ? `${API_URL}/api/orders/payment-info/${encodeURIComponent(orderId)}` : `/api/orders/payment-info/${encodeURIComponent(orderId)}`;
      const res = await adminFetch(url);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPaymentDetailsCache(prev => ({ ...prev, [orderId]: { loading: false, data: null, error: (data as { error?: string }).error || `HTTP ${res.status}` } }));
        return;
      }
      setPaymentDetailsCache(prev => ({ ...prev, [orderId]: { loading: false, data: data as PaymentInfoResponse, error: null } }));
    } catch (e) {
      setPaymentDetailsCache(prev => ({ ...prev, [orderId]: { loading: false, data: null, error: e instanceof Error ? e.message : 'Failed to load' } }));
    }
  }, []);

  // Delete order (for test orders)
  const deleteOrder = async (orderId: string, orderNumber: string) => {
    if (!confirm(`Delete order ${orderNumber}? This cannot be undone.`)) {
      return;
    }

    try {
      const orderUrl = API_URL ? `${API_URL}/api/orders/${orderId}` : `/api/orders/${orderId}`;
      const response = await adminFetch(orderUrl, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete order');

      // Remove from local state
      setOrders(orders.filter(o => o.id !== orderId));
      setExpandedOrder(null);

      // Refresh stats
      const statsUrl = API_URL ? `${API_URL}/api/orders/stats` : '/api/orders/stats';
      const statsRes = await adminFetch(statsUrl);
      if (statsRes.ok) {
        setStats(normalizeOrderStats(await statsRes.json()));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete order';
      alert(`❌ ${message}\n\nTip: Try refreshing the page and try again.`);
    }
  };

  const closureDateSet = new Set(closures.map((c) => (c.closure_date || '').slice(0, 10)));

  // Search/status/closed-day filters apply to the currently loaded archive rows.
  const filteredOrders = orders.filter(order => {
    const q = searchQuery.trim().toLowerCase();
    const matchesSearch =
      q.length === 0 ||
      String(order.order_number ?? '').toLowerCase().includes(q) ||
      String(order.customer_name ?? '').toLowerCase().includes(q) ||
      String(order.customer_email ?? '').toLowerCase().includes(q);
    // Completed list: show server cohort (past-pickup paid pipeline + completed), not only `status === 'completed'`.
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'completed' ? orderMatchesCompletedAdminCohort(order) : order.status === statusFilter);
    const matchesClosedDay =
      !showClosedDayOnly || orderPickupOnClosedDay(order, closureDateSet, businessHours);
    return matchesSearch && matchesStatus && matchesClosedDay;
  });

  // Pickup = calendar day; created_at = full ISO — both format correctly via parseCalendarOrIso
  const formatDate = (s: string) =>
    formatCalendarDateEnCA(s, { weekday: 'short', month: 'short', day: 'numeric' });

  // Format time
  const formatTime = (dateStr: string) => {
    return parseCalendarOrIso(dateStr).toLocaleTimeString('en-CA', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Fallback counts when stats API not loaded yet (prefer stats — global, not “loaded page” only)
  const pendingLoaded = orders.filter((o) => o.status === 'pending').length;
  const approvalLoaded = orders.filter((o) => String(o.status) === 'pending_approval').length;
  const pendingAttention = stats != null ? (stats.pending ?? 0) : pendingLoaded;
  const approvalAttention = stats != null ? (stats.pending_approval ?? 0) : approvalLoaded;
  // Today's pickups — local calendar day vs stored YYYY-MM-DD (not UTC midnight)
  const todayStr = localYyyyMmDd();
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = localYyyyMmDd(tomorrow);
  const todayPickups = orders.filter(o => (o.pickup_date || '').slice(0, 10) === todayStr && o.status !== 'cancelled').length;
  const closureToday = closures.find(c => (c.closure_date || '').slice(0, 10) === todayStr);
  const closureTomorrow = closures.find(c => (c.closure_date || '').slice(0, 10) === tomorrowStr);
  const closedDayLoadedCount = orders.filter((o) =>
    orderPickupOnClosedDay(o, closureDateSet, businessHours)
  ).length;

  // Time since last refresh
  const getTimeSinceRefresh = () => {
    const seconds = Math.floor((new Date().getTime() - lastRefresh.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  return (
    <div className="space-y-6" onClick={handlePageClickForSound} role="presentation">
      {/* One-time: click anywhere to enable order sounds (browser policy) */}
      {soundEnabled && !soundUnlocked && (
        <div className="bg-purple-100 border border-purple-300 rounded-xl px-4 py-2.5 text-purple-800 text-sm flex items-center gap-2">
          <HiVolumeUp className="w-5 h-5 shrink-0" />
          <span>Click anywhere on this page once to enable order sounds (required by your browser).</span>
        </div>
      )}

      {/* Flashing popups — green = order, blue = message (sound + browser alerts unchanged). */}
      <AdminFlashAlert
        kind="order"
        open={orderFlash.open}
        count={orderFlash.count}
        onDismiss={dismissOrderFlash}
        extraAction={
          pendingPrintOrder
            ? {
                label: 'Print new order',
                onClick: () => {
                  openOrderPrintWindow(pendingPrintOrder);
                  setPendingPrintOrder(null);
                },
              }
            : undefined
        }
      />
      <AdminFlashAlert
        kind="message"
        open={messageFlash.open}
        count={messageFlash.count}
        onDismiss={dismissMessageFlash}
      />

      {/* High-priority action center (persists if sound is missed / print dialog blocked). */}
      {!loading && (unseenNewOrders > 0 || unseenNewMessages > 0 || pendingPrintOrder) && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 flex flex-wrap items-center gap-2 text-sm">
          {unseenNewOrders > 0 && (
            <button
              type="button"
              onClick={() => {
                applyStatusFilter('pending');
                setUnseenNewOrders(0);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 font-semibold"
            >
              <HiBell className="w-4 h-4" />
              {unseenNewOrders} new order{unseenNewOrders > 1 ? 's' : ''} — open pending
            </button>
          )}
          {unseenNewMessages > 0 && (
            <Link
              to="/admin/messages"
              onClick={() => setUnseenNewMessages(0)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-semibold"
            >
              <HiInbox className="w-4 h-4" />
              {unseenNewMessages} new message{unseenNewMessages > 1 ? 's' : ''} — open Messages
            </Link>
          )}
          {pendingPrintOrder && (
            <>
              <button
                type="button"
                onClick={() => {
                  openOrderPrintWindow(pendingPrintOrder);
                  setPendingPrintOrder(null);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-semibold"
              >
                <HiPrinter className="w-4 h-4" />
                Print {pendingPrintOrder.order_number || 'new order'}
              </button>
              <button
                type="button"
                onClick={() => setPendingPrintOrder(null)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 font-medium"
              >
                Dismiss print reminder
              </button>
            </>
          )}
          <span className="text-xs text-red-700">
            If auto-print does not start, this button is your manual fallback.
          </span>
        </div>
      )}

      {/* PENDING ORDERS ALERT - Always visible when there are pending orders */}
      {pendingAttention > 0 && !loading && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-4 text-white flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <HiExclamation className="w-6 h-6" />
            </div>
            <div>
              <p className="font-bold text-lg">
                {pendingAttention} Pending Order{pendingAttention > 1 ? 's' : ''}!
              </p>
              <p className="text-amber-100 text-sm">
                Orders waiting for your confirmation
              </p>
            </div>
          </div>
          <button
            onClick={() => applyStatusFilter('pending')}
            className="px-4 py-2 bg-white text-amber-600 rounded-lg font-medium hover:bg-amber-50 transition-colors"
          >
            View Pending
          </button>
        </div>
      )}

      {/* Need attention strip - contact messages, closures, promos, today's pickups, pending/approval */}
      {!loading && (
        (contactStats.new > 0 || closureToday || closureTomorrow || promosEndingSoon.length > 0 || todayPickups > 0 || pendingAttention > 0 || approvalAttention > 0) && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2 text-sm">
            {contactStats.new > 0 && (
              <Link
                to="/admin/messages"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors font-medium"
              >
                <HiInbox className="w-4 h-4" />
                {contactStats.new} new message{contactStats.new > 1 ? 's' : ''}
              </Link>
            )}
            {closureToday && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 font-medium">
                📅 You&apos;re closed today{closureToday.label ? ` (${closureToday.label})` : ''}
              </span>
            )}
            {closureTomorrow && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                📅 Tomorrow: closed{closureTomorrow.label ? ` (${closureTomorrow.label})` : ''}
              </span>
            )}
            {promosEndingSoon.length > 0 && (
              <Link
                to="/admin/promos"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pink-100 text-pink-800 hover:bg-pink-200 transition-colors font-medium"
              >
                <HiTag className="w-4 h-4" />
                {promosEndingSoon[0].title} ends in{' '}
                {promosEndingSoon[0].end_date
                  ? Math.max(0, calendarDaysFromToday(promosEndingSoon[0].end_date))
                  : 0}{' '}
                days
              </Link>
            )}
            {todayPickups > 0 && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-100 text-green-800 font-medium">
                <HiCalendar className="w-4 h-4" />
                Today: {todayPickups} pickup{todayPickups > 1 ? 's' : ''}
              </span>
            )}
            {pendingAttention > 0 && (
              <button
                type="button"
                onClick={() => applyStatusFilter('pending')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors font-medium"
              >
                {pendingAttention} pending confirmation
              </button>
            )}
            {approvalAttention > 0 && (
              <button
                type="button"
                onClick={() => applyStatusFilter('pending_approval')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors font-medium"
              >
                🎂 {approvalAttention} custom cake{approvalAttention > 1 ? 's' : ''} need approval
              </button>
            )}
          </div>
        )
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
          <p className="text-gray-500 mt-1">
            Manage customer orders and track pickups
            <span className="text-xs ml-2 text-gray-400">
              (Updated: {getTimeSinceRefresh()})
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/admin/messages"
            className="inline-flex items-center gap-1.5 min-h-[44px] px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700"
          >
            <HiInbox className="w-4 h-4" />
            Messages
            {contactStats.new > 0 && (
              <span className="bg-blue-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                {contactStats.new}
              </span>
            )}
          </Link>
          <AdminAlertSoundToggle
            showTestButton
            onTestSound={handleTestSound}
            onBeforeToggle={() => {
              unlockAudio();
              audioUnlockedOnceRef.current = true;
              setSoundUnlocked(true);
            }}
          />

          {/* Auto-print new orders - opens print dialog when a new order arrives */}
          <button
            type="button"
            onClick={() => setAutoPrintNewOrders(!autoPrintNewOrders)}
            className={`min-h-[44px] px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              autoPrintNewOrders ? 'bg-amber-100 border-amber-300 text-amber-800' : 'bg-gray-100 border-gray-300 text-gray-500'
            }`}
            title={
              autoPrintNewOrders
                ? 'Auto-print ON (saved on this device) — new orders open the print dialog'
                : 'Auto-print OFF — tap to enable and save on this device'
            }
          >
            {autoPrintNewOrders ? '🖨️ Auto-print' : '🖨️ Print'}
          </button>
          
          {/* Auto-refresh Toggle */}
          <button
            onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
            className={`min-h-[44px] px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
              autoRefreshEnabled 
                ? 'bg-green-100 border-green-300 text-green-700' 
                : 'bg-gray-100 border-gray-300 text-gray-500'
            }`}
            title={autoRefreshEnabled ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          >
            {autoRefreshEnabled ? '🟢 Auto' : '⚪ Manual'}
          </button>
          
          {/* Manual Refresh */}
          <button
            onClick={() => fetchOrders()}
            className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 py-2 bg-purple-600 text-white 
                       rounded-lg hover:bg-purple-700 font-medium flex-1 sm:flex-none"
          >
            <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      {statsLoadWarning && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 text-sm text-amber-950 flex flex-wrap items-center justify-between gap-2">
          <span>{statsLoadWarning}</span>
          <button
            type="button"
            onClick={() => fetchOrders(true)}
            className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700"
          >
            Refresh
          </button>
        </div>
      )}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {(['pending', 'pending_approval', 'confirmed', 'ready', 'completed', 'cancelled'] as const).map(status => {
            const count = stats[status] ?? 0;
            const cfg = statusConfig[status];
            return (
              <button
                key={status}
                type="button"
                title={cfg.tileTitle}
                aria-label={`${cfg.label}: ${count}. ${cfg.tileTitle}`}
                onClick={() => applyStatusFilter(statusFilter === status ? 'all' : status)}
                className={`p-3 rounded-xl transition-all text-left ${cfg.bg} ${
                  statusFilter === status ? 'ring-2 ring-purple-500 shadow-md' : ''
                } ${status === 'pending' && (stats.pending ?? 0) > 0 ? 'animate-pulse ring-2 ring-amber-400' : ''} ${status === 'pending_approval' && (stats.pending_approval ?? 0) > 0 ? 'ring-2 ring-purple-400' : ''}`}
              >
                <p className={`text-2xl font-bold ${cfg.color}`}>
                  {count}
                </p>
                <p className="text-xs text-gray-600">{cfg.label}</p>
              </button>
            );
          })}
        </div>
      )}

      {/* Completed tile = status completed + paid rows whose pickup calendar day is already past (Toronto) */}
      {stats && (
        <p className="text-xs text-gray-500 -mt-1 mb-1 max-w-4xl">
          <strong className="text-gray-600">Click a tile</strong> to list those orders (switches history to{' '}
          <strong>All orders</strong> so past pickups are not hidden).{' '}
          <strong className="text-gray-600">Completed</strong> includes orders marked <strong>Complete</strong> and{' '}
          <strong>paid</strong> orders still showing Confirmed/Ready/Pending when their <strong>pickup date</strong> is{' '}
          <strong>before today</strong> (bakery time, Toronto). Use the history dropdown for{' '}
          <strong>Last 30 days</strong> or <strong>Today</strong> for day-to-day work.
        </p>
      )}

      {/* Filters + order history window (Toronto “today”, rolling 7–365 days, paid + merges) */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search loaded orders (#, name, email)…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg 
                       focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="relative">
            <HiFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={statusFilter}
              onChange={(e) => applyStatusFilter(e.target.value as OrderStatus | 'all')}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg 
                       focus:ring-2 focus:ring-purple-500 appearance-none bg-white"
              aria-label="Filter orders by status"
              title="Filter orders by status"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="pending_approval">Needs Approval</option>
              <option value="confirmed">Confirmed</option>
              <option value="ready">Ready for Pickup</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div className="relative sm:min-w-[180px]">
            <label htmlFor="order-history-range" className="sr-only">
              Order history period
            </label>
            <select
              id="order-history-range"
              value={historyRange}
              onChange={(e) => onHistoryRangeChange(e.target.value as HistoryRange)}
              className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg 
                       focus:ring-2 focus:ring-purple-500 appearance-none bg-white text-sm"
              title="Orders placed in this window (by placement date); paid rows with pickup in range but placed earlier are merged in. All orders = since 2025 (shop open). Server loads the full merged list in chunks—use Load more or “All paid / picked up” to pull every row into the browser."
            >
              <option value="all">All orders (since 2025)</option>
              <option value="today">Today (Toronto)</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 12 months</option>
              <option value="1095">Last 36 months</option>
            </select>
          </div>
          <button
            type="button"
            onClick={toggleClosedDayFilter}
            disabled={loading}
            aria-pressed={showClosedDayOnly}
            title="Show only orders whose pickup date falls on a configured closed day. Opens the full archive so past orders remain available."
            className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium whitespace-nowrap disabled:opacity-50 ${
              showClosedDayOnly
                ? 'bg-purple-600 border-purple-600 text-white'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <HiCalendar className="w-5 h-5 shrink-0" aria-hidden />
            Closed-day pickups
            {closedDayLoadedCount > 0 ? ` (${closedDayLoadedCount})` : ''}
          </button>
          <button
            type="button"
            onClick={() => void loadPaidPickedUpArchive()}
            disabled={loading || loadingPaidArchive}
            title="Sets history to All orders (since 2025), filters to Completed (paid + picked up / past pickup), then loads every page until nothing is left—matches what the database returns for that cohort."
            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-emerald-300 
              bg-emerald-50 text-emerald-900 text-sm font-medium hover:bg-emerald-100 disabled:opacity-50 whitespace-nowrap"
          >
            <HiClipboardList className="w-5 h-5 shrink-0" aria-hidden />
            {loadingPaidArchive ? 'Loading paid history…' : 'All paid / picked up'}
          </button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-gray-600">
          <p>
            Showing <strong>{filteredOrders.length}</strong> loaded
            {historyTotal > 0 && (
              <>
                {' '}
                · <strong>{historyTotal}</strong> orders in this period (
                {historyRange === 'all'
                  ? 'website paid + unpaid archive + Square'
                  : 'paid website + Square'}
                )
              </>
            )}
          </p>
          {ordersHasMore && (
            <button
              type="button"
              disabled={loadingMore}
              onClick={() => fetchOrders(false, { append: true })}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-purple-300 
                bg-purple-50 text-purple-800 text-sm font-medium hover:bg-purple-100 disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Load more orders'}
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading orders...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredOrders.length === 0 && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <HiShoppingCart className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {orders.length === 0 ? 'No Orders in This View' : 'No Matching Orders'}
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {orders.length === 0
              ? statusFilter !== 'all' && stats && (stats[statusFilter] ?? 0) > 0
                ? `The ${statusConfig[statusFilter].label} tile shows ${stats[statusFilter]} order(s), but none loaded in this history window. Open All orders or click the tile again.`
                : historyRange === 'today'
                  ? 'Nothing for today yet — upcoming Confirmed/Ready pickups still appear here. For past pickups, choose Last 30 days or click the Completed tile.'
                  : 'Orders will appear here when customers place orders through your website.'
              : 'Try adjusting your search or filter.'}
          </p>
          {orders.length === 0 &&
            statusFilter !== 'all' &&
            historyRange !== 'all' &&
            stats &&
            (stats[statusFilter] ?? 0) > 0 && (
            <button
              type="button"
              onClick={() => applyStatusFilter(statusFilter)}
              className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700"
            >
              Show all {statusConfig[statusFilter].label.toLowerCase()} orders
            </button>
          )}
          {orders.length === 0 && statusFilter === 'all' && historyRange === 'today' && (
            <button
              type="button"
              onClick={() => onHistoryRangeChange('30')}
              className="mt-4 inline-flex items-center justify-center px-4 py-2 rounded-lg border border-purple-300 bg-purple-50 text-purple-800 text-sm font-medium hover:bg-purple-100"
            >
              View last 30 days
            </button>
          )}
        </div>
      )}

      {/* Orders List */}
      {!loading && filteredOrders.length > 0 && (
        <div className="space-y-3">
          {filteredOrders.map(order => (
            <div
              key={order.id}
              className={`bg-white rounded-xl border overflow-hidden transition-all ${
                order.status === 'pending' 
                  ? 'border-amber-300 shadow-md ring-1 ring-amber-200' 
                  : 'border-gray-200'
              }`}
            >
              {/* Order Header — stacked on iPhone so badges/total stay readable */}
              <div
                className={`p-4 cursor-pointer hover:bg-gray-50 active:bg-gray-100 ${
                  order.status === 'pending' ? 'bg-amber-50' : ''
                }`}
                onClick={() => {
                  const next = expandedOrder === order.id ? null : order.id;
                  setExpandedOrder(next);
                  // On iPhone, scroll Print / Ready into view after expand.
                  if (next) {
                    window.setTimeout(() => {
                      const el = document.getElementById(`order-actions-${next}`);
                      // jsdom has no scrollIntoView — guard for tests + older WebViews.
                      if (el && typeof el.scrollIntoView === 'function') {
                        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                      }
                    }, 50);
                  }
                }}
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-col gap-2 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-mono font-bold text-purple-600 text-base">{order.order_number}</p>
                      {order.source === 'square' ? (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          In-Store
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          Online
                        </span>
                      )}
                      <span className={`px-2.5 py-1 rounded-full text-xs sm:text-sm font-medium ${statusConfig[order.status].bg} ${statusConfig[order.status].color}`}>
                        {statusConfig[order.status].label}
                      </span>
                      {isPaid(order) ? (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          ✓ Paid
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Not paid
                        </span>
                      )}
                      {order.status === 'pending' && (
                        <span className="text-amber-600 text-sm font-medium animate-pulse">
                          ⚡ Action needed
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 font-medium truncate">{order.customer_name}</p>
                  </div>
                  <div className="flex items-center justify-between sm:justify-end gap-3">
                    <div className="sm:text-right">
                      <p className="font-bold text-lg">${order.total.toFixed(2)}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1 sm:justify-end">
                        <HiCalendar className="w-4 h-4 shrink-0" />
                        {formatDate(order.pickup_date)}
                      </p>
                    </div>
                    {expandedOrder === order.id ? (
                      <HiChevronUp className="w-6 h-6 text-gray-400 shrink-0" />
                    ) : (
                      <HiChevronDown className="w-6 h-6 text-gray-400 shrink-0" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedOrder === order.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
                  {/* Payment status - prominent so Tammy always knows */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">Payment:</span>
                    {isPaid(order) ? (
                      <span className="px-3 py-1.5 rounded-lg text-sm font-medium bg-green-100 text-green-800">
                        ✓ Paid
                      </span>
                    ) : (
                      <span className="px-3 py-1.5 rounded-lg text-sm font-medium bg-red-100 text-red-800">
                        Not paid
                      </span>
                    )}
                  </div>

                  {/* Payment & transaction details dropdown - for Tammy when she needs proof/details */}
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const open = paymentDetailsOpen === order.id ? null : order.id;
                        setPaymentDetailsOpen(open);
                        if (open) fetchPaymentInfo(order.id);
                      }}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <span>Payment & transaction details</span>
                      {paymentDetailsOpen === order.id ? (
                        <HiChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <HiChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {paymentDetailsOpen === order.id && (
                      <div className="px-3 pb-3 pt-0 border-t border-gray-100">
                        {paymentDetailsCache[order.id]?.loading && (
                          <p className="text-sm text-gray-500 py-2">Loading...</p>
                        )}
                        {paymentDetailsCache[order.id]?.error && (
                          <p className="text-sm text-red-600 py-2">{paymentDetailsCache[order.id].error}</p>
                        )}
                        {paymentDetailsCache[order.id]?.data && !paymentDetailsCache[order.id]?.loading && (
                          <div className="text-sm space-y-2 pt-2">
                            <p><span className="text-gray-500">Source:</span> <span className="font-medium">{paymentDetailsCache[order.id].data!.source ?? '—'}</span></p>
                            <p><span className="text-gray-500">Payment status:</span> <span className="font-medium">{paymentDetailsCache[order.id].data!.payment_status ?? '—'}</span></p>
                            {paymentDetailsCache[order.id].data!.transaction && (
                              <>
                                {paymentDetailsCache[order.id].data!.transaction!.id && (
                                  <p><span className="text-gray-500">Transaction ID:</span> <span className="font-mono text-xs">{paymentDetailsCache[order.id].data!.transaction!.id}</span></p>
                                )}
                                {paymentDetailsCache[order.id].data!.transaction!.status && (
                                  <p><span className="text-gray-500">Status:</span> <span className="font-medium">{paymentDetailsCache[order.id].data!.transaction!.status}</span></p>
                                )}
                                {paymentDetailsCache[order.id].data!.transaction!.amount?.amount != null && (
                                  <p><span className="text-gray-500">Amount:</span> ${((paymentDetailsCache[order.id].data!.transaction!.amount!.amount ?? 0) / 100).toFixed(2)} {paymentDetailsCache[order.id].data!.transaction!.amount!.currencyCode ?? ''}</p>
                                )}
                                {paymentDetailsCache[order.id].data!.transaction!.receiptUrl && (
                                  <p>
                                    <a href={paymentDetailsCache[order.id].data!.transaction!.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">
                                      View receipt (Square)
                                    </a>
                                  </p>
                                )}
                                {paymentDetailsCache[order.id].data!.transaction!.tenders?.length ? (
                                  <div>
                                    <p className="text-gray-500 mb-1">Tenders (in-store):</p>
                                    <ul className="list-disc list-inside text-gray-700">
                                      {paymentDetailsCache[order.id].data!.transaction!.tenders!.map((t, i) => (
                                        <li key={i}>
                                          {t.amount?.amount != null ? `$${(Number(t.amount.amount) / 100).toFixed(2)}` : '—'} {t.type ?? ''} {t.createdAt ?? ''}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                              </>
                            )}
                            {paymentDetailsCache[order.id].data!.transactionError && (
                              <p className="text-amber-600">{paymentDetailsCache[order.id].data!.transactionError}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Customer Info */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <a href={`mailto:${order.customer_email}`} className="flex items-center gap-1 text-purple-600 hover:underline">
                      <HiMail className="w-4 h-4" />
                      {order.customer_email}
                    </a>
                    {order.customer_phone && (
                      <a href={`tel:${order.customer_phone}`} className="flex items-center gap-1 text-purple-600 hover:underline">
                        <HiPhone className="w-4 h-4" />
                        {order.customer_phone}
                      </a>
                    )}
                    {(order.pickup_date || order.pickup_time) && (
                      <span className="flex items-center gap-1 text-gray-600">
                        <HiClock className="w-4 h-4" />
                        Pickup: {order.pickup_date ? formatDate(order.pickup_date) : ''}{order.pickup_date && order.pickup_time ? ' at ' : ''}{order.pickup_time || ''}
                      </span>
                    )}
                  </div>

                  {/* Items */}
                  <div className="bg-white rounded-lg border p-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Order Items</p>
                    <div className="space-y-1">
                      {order.items.map((item, idx) => {
                        const note = item.note ?? item.notes;
                        const customizationsStr = formatItemCustomizations(item.customizations);
                        return (
                          <div key={idx} className="flex justify-between gap-2 text-sm">
                            <div className="min-w-0 flex-1">
                              <span>{item.quantity}x {item.name}</span>
                              {note && (
                                <p className="text-xs text-amber-700 mt-0.5 truncate" title={note}>
                                  {note}
                                </p>
                              )}
                              {customizationsStr && (
                                <p className="text-xs text-slate-600 mt-0.5 truncate" title={customizationsStr}>
                                  {customizationsStr}
                                </p>
                              )}
                            </div>
                            <span className="text-gray-600 flex-shrink-0">${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        );
                      })}
                      <div className="border-t pt-2 mt-2 flex justify-between text-sm">
                        <span>Subtotal</span>
                        <span>${order.subtotal.toFixed(2)}</span>
                      </div>
                      {(order.hst ?? order.tax ?? 0) > 0 && (() => {
                        const taxable = getTaxableSubtotalFromItems(order.items);
                        return (
                          <div className="flex justify-between text-sm text-gray-500">
                            <span>Taxable amount (before HST)</span>
                            <span>${taxable.toFixed(2)}</span>
                          </div>
                        );
                      })()}
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Tax (HST)</span>
                        <span>${(order.hst ?? order.tax ?? 0).toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">HST 13% on taxable items only. 6+ baked items or cakes exempt. Sandwiches always taxable.</p>
                      <div className="flex justify-between font-bold">
                        <span>Total</span>
                        <span>${order.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Cake details (custom cake orders) */}
                  {order.cake_details && (() => {
                    let cake: CakeDetails | null = null;
                    try {
                      const parsed = typeof order.cake_details === 'string' ? JSON.parse(order.cake_details) : order.cake_details;
                      if (parsed && typeof parsed === 'object') cake = parsed;
                    } catch {
                      return null;
                    }
                    if (!cake) return null;
                    return (
                      <div className="bg-purple-50 rounded-lg border border-purple-200 p-3">
                        <p className="text-sm font-medium text-purple-800 mb-2">🎂 Cake details</p>
                        <ul className="text-sm text-purple-700 space-y-1">
                          {cake.size && <li><strong>Size:</strong> {cake.size}</li>}
                          {cake.flavor && <li><strong>Flavor:</strong> {cake.flavor}</li>}
                          {cake.filling && <li><strong>Filling:</strong> {cake.filling}</li>}
                          {cake.occasion && <li><strong>Occasion:</strong> {cake.occasion}</li>}
                          {cake.cakeMessage && <li><strong>Message on cake:</strong> &quot;{cake.cakeMessage}&quot;</li>}
                          {cake.designNotes && <li><strong>Design:</strong> {cake.designNotes}</li>}
                          {cake.servings && <li><strong>Servings:</strong> {cake.servings}</li>}
                          {cake.dietaryNotes && <li><strong>Dietary:</strong> {cake.dietaryNotes}</li>}
                        </ul>
                      </div>
                    );
                  })()}

                  {/* Special Instructions */}
                  {order.special_instructions && (
                    <div className="bg-amber-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-amber-800">Special Instructions</p>
                      <p className="text-sm text-amber-700 whitespace-pre-wrap">{order.special_instructions}</p>
                    </div>
                  )}

                  {/* Print + status — thumb-friendly full-width on iPhone */}
                  <div id={`order-actions-${order.id}`} className="flex flex-col gap-3 scroll-mt-24">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openOrderPrintWindow(order);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 min-h-[52px] px-5 py-3.5 bg-indigo-600 text-white rounded-xl text-base font-semibold hover:bg-indigo-700 shadow-md border border-indigo-700"
                    >
                      <HiPrinter className="w-6 h-6 shrink-0" />
                      Print order
                    </button>

                    {/* Status Actions — Ready stays clear; Complete is quiet to avoid mis-taps */}
                    <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2">
                      <span className="text-sm text-gray-500">Update status:</span>
                      {/* Custom cakes: payment often happens after approval — move into normal pipeline */}
                      {order.status === 'pending_approval' && (
                        <button
                          type="button"
                          onClick={() => updateStatus(order.id, 'confirmed')}
                          className="w-full sm:w-auto min-h-[44px] px-4 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                        >
                          Approve custom cake
                        </button>
                      )}
                      {order.status === 'pending' && (
                        <button
                          type="button"
                          onClick={() => updateStatus(order.id, 'confirmed')}
                          className="w-full sm:w-auto min-h-[44px] px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          Confirm Order
                        </button>
                      )}
                      {order.status === 'confirmed' && (
                        <button
                          type="button"
                          onClick={() => updateStatus(order.id, 'ready')}
                          className="w-full sm:w-auto min-h-[48px] px-4 py-2.5 bg-emerald-700 text-white rounded-lg text-sm font-semibold hover:bg-emerald-800"
                          title="Emails the customer that their order is ready"
                        >
                          Mark Ready for Pickup
                        </button>
                      )}
                      {order.status === 'ready' && (
                        <>
                          <button
                            type="button"
                            onClick={() => updateStatus(order.id, 'ready', { resendEmail: true })}
                            className="w-full sm:w-auto min-h-[44px] px-3 py-2 text-sm font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100"
                            title="Send the ready-for-pickup email again"
                          >
                            Resend ready email
                          </button>
                          <button
                            type="button"
                            onClick={() => updateStatus(order.id, 'completed')}
                            className="w-full sm:w-auto min-h-[44px] px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Mark as picked up
                          </button>
                        </>
                      )}
                      {order.status !== 'cancelled' && order.status !== 'completed' && (
                        <button
                          type="button"
                          onClick={() => updateStatus(order.id, 'cancelled')}
                          className="w-full sm:w-auto min-h-[44px] px-3 py-2 text-sm text-red-700 bg-white border border-red-200 rounded-lg hover:bg-red-50"
                        >
                          Cancel
                        </button>
                      )}

                      {/* Delete — kept available but not competing with Print / Ready */}
                      <button
                        type="button"
                        onClick={() => deleteOrder(order.id, order.order_number)}
                        className="w-full sm:w-auto min-h-[44px] px-3 py-2 text-sm text-red-700 bg-white border border-red-200 rounded-lg hover:bg-red-50 sm:ml-auto"
                      >
                        Delete
                      </button>
                    </div>
                    {order.status === 'confirmed' && (
                      <p className="text-xs text-gray-500">
                        Mark Ready for Pickup emails the customer. Use Print first while you pack.
                      </p>
                    )}
                  </div>

                  {/* Order Meta */}
                  <p className="text-xs text-gray-400">
                    Ordered: {formatDate(order.created_at)} at {formatTime(order.created_at)}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Help - notification tips (collapsed: keeps focus on live orders) */}
      <div className="bg-purple-50 rounded-xl border border-purple-100 text-sm text-purple-800 overflow-hidden">
        <button
          type="button"
          onClick={() => setNeverMissHelpOpen((o) => !o)}
          className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left font-medium hover:bg-purple-100/60 transition-colors"
        >
          <span>🔔 Never miss an order — sound, print &amp; history tips</span>
          {neverMissHelpOpen ? (
            <HiChevronUp className="w-5 h-5 shrink-0 text-purple-600" aria-hidden />
          ) : (
            <HiChevronDown className="w-5 h-5 shrink-0 text-purple-600" aria-hidden />
          )}
        </button>
        {neverMissHelpOpen && (
          <ul className="space-y-1 text-purple-700 px-4 pb-4 pt-0">
            <li>• <strong>Auto-refresh:</strong> Page updates every 30 seconds</li>
            <li>
              • <strong>Loop sound:</strong> Purple volume icon in the top bar (every admin page) or on Orders — ON repeats the alarm/chime until you dismiss the popup; OFF keeps flash popups and browser alerts without sound. Tap once on Orders or <strong>Test sound</strong> to unlock audio in your browser.
            </li>
            <li>
              • <strong>Flash popups:</strong> A big <strong>green</strong> popup = new order; <strong>blue</strong> popup = new contact message.
              They stay on screen until Tammy taps <strong>Dismiss</strong>, <strong>Close</strong>, or the dark backdrop — the alarm/chime
              keeps repeating until then too. On other admin pages the same popups appear automatically.
              Sound, browser alerts, and the red Action Center strip still work as before.
            </li>
            <li>
              • <strong>Print:</strong> Uses a hidden print sheet first. If the print dialog does not open, allow{' '}
              <strong>pop-ups</strong> for this site and try again. Same flow on any laptop, desktop, or
              Chromebook—your browser’s usual print box.
            </li>
            <li>
              • <strong>History:</strong> Default <strong>Today</strong> shows placed today, today&apos;s pickups, and{' '}
              <strong>upcoming</strong> Confirmed/Ready/Pending work. <strong>Click a status tile</strong> (Confirmed,
              Completed, …) to list those orders — history switches to <strong>All orders</strong> so past pickups are
              not hidden. Rolling presets show <strong>paid</strong> website orders (plus Square), by{' '}
              <strong>when the order was placed</strong>, with pickup-window merge. <strong>All orders (since 2025)</strong>{' '}
              adds <strong>unpaid</strong> website rows too. Use <strong>Last 30 days</strong> or green{' '}
              <strong>All paid / picked up</strong> for past pickups. Status tile <strong>counts</strong> are{' '}
              <strong>all-time paid</strong> exact totals. Search only covers loaded rows.
            </li>
            <li>
              • <strong>Auto-print:</strong> Turn on &quot;🖨️ Auto-print&quot; to open the print dialog when a new order
              is detected. Browsers may still block silent printing; if so, use the red Action Center “Print” button.
            </li>
            <li>• <strong>Browser alerts:</strong> Get notifications even in other tabs</li>
            <li>• <strong>Orange highlight:</strong> Pending orders stand out</li>
            <li>
              • <strong>Auto-complete:</strong> Paid orders in <strong>Pending</strong>, <strong>Confirmed</strong>, or{' '}
              <strong>Ready</strong> are set to <strong>Completed</strong> automatically once the{' '}
              <strong>pickup date</strong> has passed (bakery day in <strong>Toronto</strong> time). No extra email is
              sent. This keeps counts accurate if you forget to tap Complete after pickup.
            </li>
          </ul>
        )}
      </div>

      {/* Phone Orders Note */}
      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
        <p className="font-medium mb-1">📞 Taking Phone Orders?</p>
        <p>
          Customers can still call <strong>(705) 503-4966</strong> to place orders. 
          You can manually track those orders here or in your Square POS.
        </p>
      </div>
    </div>
  );
}
